import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ImageCacheService {
  private imageCache: Map<string, HTMLImageElement> = new Map();
  private colorCache = new Map<string, string>();

  preloadImage(url: string): Promise<HTMLImageElement> {
    // Return from cache if available
    if (this.imageCache.has(url)) {
      return Promise.resolve(this.imageCache.get(url)!);
    }

    // Otherwise load the image
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.imageCache.set(url, img);
        resolve(img);
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  getDominantColor(imageUrl: string, fallbackColor: string = 'rgba(240, 240, 240, 0.9)'): string {
    if (this.colorCache.has(imageUrl)) {
      return this.colorCache.get(imageUrl);
    }

    return fallbackColor;
  }

  extractAndCacheDominantColor(img: HTMLImageElement, imageUrl: string): string {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Scale down for performance
      canvas.width = 50;
      canvas.height = 50;

      ctx.drawImage(img, 0, 0, 50, 50);
      const imageData = ctx.getImageData(0, 0, 50, 50).data;

      // Simple algorithm to average the colors
      let r = 0, g = 0, b = 0, count = 0;

      for (let i = 0; i < imageData.length; i += 4) {
        r += imageData[i];
        g += imageData[i+1];
        b += imageData[i+2];
        count++;
      }

      r = Math.floor(r / count);
      g = Math.floor(g / count);
      b = Math.floor(b / count);

      const color = `rgba(${r}, ${g}, ${b}, 1)`;
      this.colorCache.set(imageUrl, color);
      return color;
    } catch (e) {
      console.error('Error extracting color:', e);
      const fallback = 'rgba(240, 240, 240, 1)';
      this.colorCache.set(imageUrl, fallback);
      return fallback;
    }
  }

  clearCache(): void {
    this.imageCache.clear();
    this.colorCache.clear();
  }

  getImage(url: string): HTMLImageElement | undefined {
    return this.imageCache.get(url);
  }

  hasImage(url: string): boolean {
    return this.imageCache.has(url);
  }

  hasColor(url: string): boolean {
    return this.colorCache.has(url);
  }
}
